import Breadcrumbs from "@/components/Breadcrumbs";
import { SettingsService } from "@/services/admin-services";
import { SettingsNotification } from "@/services/admin-services/models";
import { LocaleService } from "@/services/global-services";
import { Language, Locale } from "@/services/global-services/models";
import {
    alpha,
    Box,
    Button,
    CircularProgress,
    Divider,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const breadcrumbs = [
    {
        to: "../..",
        title: "Administration",
    },
    {
        title: "Settings",
        to: "../..",
    },
    {
        title: "Notification",
        to: "",
    },
];

const Overview = () => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<string>("");
    const [data, setData] = useState<Array<SettingsNotification>>([]);
    const [languages, setLanguages] = useState<Array<Language>>([]);

    const settingsService = new SettingsService("int");

    const theme = useTheme();
    const navigate = useNavigate();

    useEffect(() => {
        Promise.all([
            LocaleService.get().then(parseLanguages),
            settingsService.getNotification().then((data) => {
                setData(data);
            }),
        ]).then(() => {
            setIsLoading(false);
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const parseLanguages = (regions: Array<Locale>) => {
        const languages: Array<Language> = [];
        for (let i = 0; i < regions.length; i++) {
            const region = regions[i];

            for (let j = 0; j < region.languages.length; j++) {
                const language = region.languages[j];
                if (languages.findIndex((x) => x.label === language.label) > -1) continue;

                const split = language.slug.split("-");
                languages.push({
                    ...language,
                    slug: `int-${split[1]}`,
                    regionLabel: "International",
                });
            }
        }

        setLanguages(languages.sort((a, b) => a.label.localeCompare(b.label)));
    };

    const renderStatus = (language: string) => {
        const notification = data.find((x) => x.language === language);
        const active = Boolean(notification);

        let statusColor = theme.palette.success;
        if (!active) statusColor = theme.palette.error;

        return (
            <Typography
                variant={"caption"}
                fontWeight={700}
                sx={{
                    bgcolor: alpha(statusColor.light, 0.1),
                    color: statusColor.dark,
                    paddingX: 1.5,
                    paddingY: 0.5,
                    borderRadius: 4,
                    display: "inline",
                }}
            >
                {active ? "Active" : "Inactive"}
            </Typography>
        );
    };

    const onClear = (id: string) => {
        setIsSaving(id);

        settingsService
            .removeNotification(id)
            .then(() => {
                const index = data.findIndex((x) => x.id === id);
                const newData = [...data];
                newData.splice(index, 1);
                setData(newData);
            })
            .finally(() => {
                setTimeout(() => setIsSaving(""), 1200);
            });
    };

    return (
        <Box>
            <Breadcrumbs trail={breadcrumbs} />

            <Divider />

            <Grid container spacing={2} p={{ xs: 2, md: 4 }}>
                <Grid size={{ xs: 12, md: 12 }}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 750 }} aria-label="simple table">
                            <TableHead sx={{ bgcolor: "alternate.main" }}>
                                <TableRow>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Notification Language
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ width: 100 }}>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Status
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ width: 200 }} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={3}
                                            sx={{
                                                height: 400,
                                                alignItems: "center",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <Box sx={{ display: "flex", placeContent: "center" }}>
                                                <CircularProgress />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    languages
                                        .sort((a, b) => a.label.localeCompare(b.label))
                                        .map((item, i) => {
                                            const notification = data.find((x) => x.language === item.slug);
                                            const hasNotification = Boolean(notification);

                                            return (
                                                <TableRow
                                                    key={i}
                                                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                                                >
                                                    <TableCell component="th" scope="row">
                                                        <Typography color={"text.secondary"} variant={"subtitle2"}>
                                                            {item.label}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell scope="row">
                                                        <Typography color={"text.secondary"} variant={"subtitle2"}>
                                                            {renderStatus(item.slug)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell scope="row" sx={{ p: 0 }}>
                                                        <Box sx={{ display: "flex" }}>
                                                            <Button
                                                                color={"primary"}
                                                                sx={{ ml: "auto", mr: 1 }}
                                                                onClick={() =>
                                                                    navigate(
                                                                        `/admin/settings/notification/${item.slug}`
                                                                    )
                                                                }
                                                                disabled={Boolean(isSaving)}
                                                            >
                                                                Edit
                                                            </Button>

                                                            <Box sx={{ position: "relative" }}>
                                                                <Button
                                                                    color={"error"}
                                                                    sx={{ mr: 1 }}
                                                                    onClick={() =>
                                                                        notification && onClear(notification.id)
                                                                    }
                                                                    disabled={!hasNotification || Boolean(isSaving)}
                                                                >
                                                                    Clear
                                                                </Button>
                                                                {isSaving === item.slug && (
                                                                    <CircularProgress
                                                                        size={24}
                                                                        sx={{
                                                                            position: "absolute",
                                                                            top: "50%",
                                                                            left: "50%",
                                                                            marginTop: "-12px",
                                                                            marginLeft: "-12px",
                                                                        }}
                                                                    />
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Overview;
